/* WHAT MOVED SINCE LAST WEEK — the delta beside every changed value (W3, 2026-09-18).
 *
 * 🔴 **HIS 2026-09-10 ASK, in his own shape:** the coach taps the assigned days and *"every moved
 * field shows its delta beside the value (`25 kg (+2.5)`, sets too, minus as well as plus)"*.
 *
 * ⚠️ **DERIVED AGAINST LAST WEEK'S ROW AT (exercise, role), NEVER STORED** — his own words, and the
 * reason is one this repo has already paid for: *"a diff cannot reconstruct a change that returns
 * to its starting value"* is true of an EDIT LOG, and the opposite mistake — storing the delta —
 * makes a second copy of a fact the rows already carry, which then disagrees with them. Two weeks
 * of rows are the record; this is a reading of them.
 *
 * ⚠️ **THE KEY IS (exercise, role), NOT POSITION.** A week that inserts a warm-up shifts every
 * position below it, and keyed on position the whole session would read as changed. The phone's
 * `CoachProgression.unitKey` is the same shape for the same reason — it contains no row id, so a
 * cloned week's keys are identical to its source's by construction.
 */

/** One prescribed row, as either week holds it. */
export type WeekRow = {
  /** The library exercise — `coach_exercise_id` or the engine's id, whichever the row carries. */
  exerciseId: string;
  /** `section_role` — a warm-up 3×10 and a main 3×10 are different prescriptions. */
  role: string | null;
  sets: number | null;
  reps: number | null;
  weightKg: number | null;
  holdSeconds: number | null;
  /**
   * W72 step 2 — the `coach_workout_sets` ids this row groups, and the `coach_workout_exercises` row it
   * is. OPTIONAL and additive: every existing reader ignores them; the proposals overlay's `Hold at 60`
   * writes exactly these set rows and nothing else, and `Change…` opens the editor on that exercise row.
   */
  setIds?: string[];
  exerciseRowId?: string;
};

/** What changed on one field. `from` is last week, `to` is this week. */
export type FieldDelta = {
  field: "sets" | "reps" | "weightKg" | "holdSeconds";
  from: number | null;
  to: number | null;
  /** `to − from`, or null when either side is absent — see `change` below. */
  change: number | null;
};

export function unitKey(row: { exerciseId: string; role: string | null }): string {
  // The role's absence is a VALUE — an unroled row is its own unit, not every unroled row merged.
  return `${row.exerciseId}::${row.role ?? "(none)"}`;
}

/**
 * The arithmetic difference, or null when there is nothing honest to subtract.
 *
 * ⚠️ **AN ABSENT VALUE IS NOT A ZERO.** `weight_kg` NULL means *nobody recorded it*, not
 * *unloaded* — `CLAUDE.md` states that rule and names the defect it caused ("loaded sets" reading
 * 0 for work that plainly has a load). So a field that went from null to 60 is a CHANGE worth
 * showing, and `+60` would be a lie about a load that was never zero: the row says *now 60 kg*
 * with no delta, which is the truth.
 */
export function change(from: number | null, to: number | null): number | null {
  if (from === null || to === null) return null;
  return to - from;
}

/** Every field of one row that differs from its counterpart last week. */
export function rowDeltas(last: WeekRow, now: WeekRow): FieldDelta[] {
  const fields: FieldDelta["field"][] = ["sets", "reps", "weightKg", "holdSeconds"];
  const out: FieldDelta[] = [];
  for (const f of fields) {
    const from = last[f];
    const to = now[f];
    if (from === to) continue;
    out.push({ field: f, from, to, change: change(from, to) });
  }
  return out;
}

export type RowComparison =
  /** Present both weeks, with what moved (possibly nothing). */
  | { kind: "changed"; row: WeekRow; deltas: FieldDelta[] }
  /** In this week and not last — the coach added it, or the writer did. */
  | { kind: "new"; row: WeekRow }
  /** In last week and not this one. */
  | { kind: "dropped"; row: WeekRow };

/**
 * Compare this week's rows against last week's.
 *
 * ⚠️ **A DUPLICATE KEY TAKES THE FIRST ROW AND SAYS SO BY NOT MERGING.** A session can legitimately
 * hold the same exercise twice in one role (a top set and a back-off, authored as two rows — the
 * shape his week 5 uses), and averaging or summing them would invent a prescription nobody wrote.
 * They are paired in order, which is what a coach reading two rows down a page expects.
 */
export function compareWeeks(lastWeek: WeekRow[], thisWeek: WeekRow[]): RowComparison[] {
  const lastByKey = new Map<string, WeekRow[]>();
  for (const r of lastWeek) {
    const k = unitKey(r);
    const list = lastByKey.get(k);
    if (list === undefined) lastByKey.set(k, [r]); else list.push(r);
  }

  const out: RowComparison[] = [];
  const takenCount = new Map<string, number>();
  for (const row of thisWeek) {
    const k = unitKey(row);
    const n = takenCount.get(k) ?? 0;
    const counterpart = lastByKey.get(k)?.[n];
    takenCount.set(k, n + 1);
    if (counterpart === undefined) out.push({ kind: "new", row });
    else out.push({ kind: "changed", row, deltas: rowDeltas(counterpart, row) });
  }

  // Anything last week had that this week does not. ⚠️ Reported rather than silently absent: a
  // dropped exercise is a coaching decision, and a week that quietly loses a row reads as a bug.
  for (const [k, rows] of lastByKey) {
    const used = takenCount.get(k) ?? 0;
    for (const row of rows.slice(used)) out.push({ kind: "dropped", row });
  }
  return out;
}

/**
 * One field's delta, as it reads beside the value: `25 kg (+2.5)`.
 *
 * ⚠️ **MINUS AS WELL AS PLUS — his own words.** A deload is the week a coach most needs to see at a
 * glance, and a view that only shows increases is one that hides exactly that.
 */
export function describeDelta(d: FieldDelta): string {
  const unit = d.field === "weightKg" ? " kg" : d.field === "holdSeconds" ? "s" : "";
  const to = d.to === null ? "—" : `${trim(d.to)}${unit}`;
  if (d.change === null) {
    // One side was absent. Saying "+60" about a load that was never zero would be a lie; saying
    // where it stands now, with no arithmetic, is not.
    return d.from === null ? `${to} (new)` : `${to} (was ${trim(d.from)}${unit})`;
  }
  if (d.change === 0) return to;
  return `${to} (${d.change > 0 ? "+" : ""}${trim(d.change)})`;
}

export const FIELD_NAME: Record<FieldDelta["field"], string> = {
  sets: "Sets",
  reps: "Reps",
  weightKg: "Weight",
  holdSeconds: "Hold",
};

function trim(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}
